/**
 * NOS-248: Debounced, coalescing purge queue.
 *
 * Rapid successive saves (canvas edits fire on every debounced keystroke) must
 * not stampede the purge API. Per org we accumulate affected paths during a
 * quiet window and flush one request; an org-wide plan swallows any queued
 * paths, and a path set that grows past `maxPaths` escalates to org-wide
 * (one tag purge is cheaper than dozens).
 *
 * Flushing is fire-and-forget: a failed purge only means stale content for at
 * most the route's s-maxage window, so it must never break a save.
 */
import type { PurgePlan } from "./purgeTriggers"

export type PurgeFlusher = (orgId: string, plan: PurgePlan) => Promise<void>

export interface PurgeQueueOptions {
  /** Quiet window after the last enqueue before flushing (ms). */
  quietMs?: number
  /** Ceiling on total wait once a burst starts, so long edit sessions still purge (ms). */
  maxWaitMs?: number
  /** Path count at which a plan escalates to an org-wide purge. */
  maxPaths?: number
}

interface OrgQueueState {
  paths: Set<string>
  orgWide: boolean
  quietTimer: ReturnType<typeof setTimeout> | null
  maxWaitTimer: ReturnType<typeof setTimeout> | null
}

export interface PurgeQueue {
  enqueue(orgId: string, plan: PurgePlan): void
  /** Flush everything now (test hook / best-effort teardown). */
  flushAll(): void
}

export function createPurgeQueue(
  flush: PurgeFlusher,
  { quietMs = 2000, maxWaitMs = 10000, maxPaths = 25 }: PurgeQueueOptions = {}
): PurgeQueue {
  const orgs = new Map<string, OrgQueueState>()

  function flushOrg(orgId: string): void {
    const state = orgs.get(orgId)
    if (!state) return
    orgs.delete(orgId)
    if (state.quietTimer) clearTimeout(state.quietTimer)
    if (state.maxWaitTimer) clearTimeout(state.maxWaitTimer)
    const plan: PurgePlan = state.orgWide
      ? { scope: "org" }
      : { scope: "paths", paths: [...state.paths] }
    if (plan.scope === "paths" && plan.paths.length === 0) return
    void flush(orgId, plan).catch((err) => {
      console.warn(`Cache purge failed for org ${orgId} (content stays stale until TTL):`, err)
    })
  }

  return {
    enqueue(orgId, plan) {
      let state = orgs.get(orgId)
      if (!state) {
        state = { paths: new Set(), orgWide: false, quietTimer: null, maxWaitTimer: null }
        orgs.set(orgId, state)
        state.maxWaitTimer = setTimeout(() => flushOrg(orgId), maxWaitMs)
      }
      if (plan.scope === "org") {
        state.orgWide = true
      } else {
        for (const p of plan.paths) state.paths.add(p)
        if (state.paths.size > maxPaths) state.orgWide = true
      }
      if (state.quietTimer) clearTimeout(state.quietTimer)
      state.quietTimer = setTimeout(() => flushOrg(orgId), quietMs)
    },
    flushAll() {
      for (const orgId of [...orgs.keys()]) flushOrg(orgId)
    },
  }
}
